import { optionValues, parseModule, parseSpecification, type BodyMeasurements, type ModuleRegistry, type PatternProject, type PatternSpecification, type RegisteredSpecification } from "@tailor-frame/engine";
import pyjamaSource from "../flat-pajama-top/v3.yaml?raw";
import pyjamaTrousersSource from "../flat-pajama-trousers/v1.yaml?raw";
import tailoredShirtSource from "../tailored-shirt-block/v1.yaml?raw";
import tShirtSource from "../flat-t-shirt/v1.yaml?raw";
import jeansSource from "../flat-jeans/v1.yaml?raw";
import trousersSource from "../flat-trousers/v1.yaml?raw";
import classicTrousersSource from "../classic-trousers/v1.yaml?raw";
import swimmingTrunksSource from "../swimming-trunks/v1.yaml?raw";
import flatShirtSource from "../flat-shirt/v1.yaml?raw";
import casualShirtSource from "../casual-shirt/v1.yaml?raw";
import overgarmentSource from "../flat-overgarment/v1.yaml?raw";
import overallsSource from "../flat-overalls/v1.yaml?raw";
import workJacketSource from "../flat-work-jacket/v1.yaml?raw";
import jeansJacketSource from "../jeans-jacket/v1.yaml?raw";
import anorakSource from "../anorak/v1.yaml?raw";
import shirtJacketSource from "../shirt-jacket/v1.yaml?raw";
import jumperSource from "../flat-jumper/v1.yaml?raw";
import classicShirtSource from "../classic-shirt/v1.yaml?raw";
import suitJacketSource from "../classic-suit-jacket/v1.yaml?raw";
import easyFitSuitJacketSource from "../easy-fit-suit-jacket/v1.yaml?raw";
import waistcoatSource from "../classic-waistcoat/v1.yaml?raw";
import casualJacketSource from "../classic-casual-jacket/v1.yaml?raw";
import easyFitCasualJacketSource from "../easy-fit-casual-jacket/v1.yaml?raw";
import overgarmentBlockSource from "../classic-overgarment/v1.yaml?raw";
import easyFitOvergarmentSource from "../easy-fit-overgarment/v1.yaml?raw";
import circularCapeSource from "../supreme-circular-cape/v1.yaml?raw";
import regularCapeSource from "../supreme-regular-cape/v1.yaml?raw";
import carriageCapeSource from "../supreme-carriage-cape/v1.yaml?raw";
import supremeTrousersSource from "../supreme-trousers/v1.yaml?raw";
import vestSource from "../supreme-vest/v1.yaml?raw";
import sackCoatSource from "../supreme-sack-coat/v1.yaml?raw";
import overcoatSource from "../supreme-overcoat/v1.yaml?raw";
import womensSkirtSource from "../womens-skirt/v1.yaml?raw";
import womensSwimsuitSource from "../womens-swimsuit/v1.yaml?raw";
import womensBodyshaperSource from "../womens-bodyshaper/v1.yaml?raw";
import womensLeggingsSource from "../womens-leggings/v1.yaml?raw";
import womensTrousersSource from "../womens-trousers/v1.yaml?raw";
import womensJeansSource from "../womens-jeans/v1.yaml?raw";
import womensBodiceSource from "../womens-bodice/v1.yaml?raw";
import womensTshirtSource from "../womens-tshirt/v1.yaml?raw";
import womensJacketSource from "../womens-jacket/v1.yaml?raw";
import womensCoatSource from "../womens-coat/v1.yaml?raw";
import womensShirtSource from "../womens-shirt/v1.yaml?raw";
import womensDressSource from "../womens-dress/v1.yaml?raw";
import frockCoatSource from "../supreme-frock-coat/v1.yaml?raw";
import ridingBreechesSource from "../supreme-riding-breeches/v1.yaml?raw";
import militaryCoatSource from "../supreme-military-coat/v1.yaml?raw";
import ridingCoatSource from "../supreme-riding-coat/v1.yaml?raw";
import slipOnSource from "../supreme-slip-on/v1.yaml?raw";
import childFlatBodySource from "../child-flat-body/v1.yaml?raw";
import childFlatOvergarmentSource from "../child-flat-overgarment/v1.yaml?raw";
import babyFlatBodySource from "../baby-flat-body/v1.yaml?raw";
import babyFlatOvergarmentSource from "../baby-flat-overgarment/v1.yaml?raw";
import babySleepsuitSource from "../baby-sleepsuit/v1.yaml?raw";
import babyTwoPieceTrousersSource from "../baby-two-piece-trousers/v1.yaml?raw";
import babyOnePieceTrousersSource from "../baby-one-piece-trousers/v1.yaml?raw";
import rectangleSource from "../fixtures/simple-rectangle.yaml?raw";
import rectangleWithFinishSource from "../fixtures/rectangle-with-finish.yaml?raw";
import { defaultProfileFor, measurementProfileLabel, measurementsForProfile, type BodyProfileId } from "./measurementProfiles";

export { bodyMeasurementFor, bodyMeasurementRows, bodyMeasurementsFor, bodyMeasurementsForProfile, defaultProfileFor, linkedMeasurements, measurementProfileFigure, measurementProfileLabel, measurementProfileStature, measurementsForProfile, bodyProfileIds, profileIdsFor, profileSizeRange, specificationSizeRange, type BodyMeasurementRow, type BodyMeasurementSet, type BodyProfileId } from "./measurementProfiles";
export { bodyTypes, bodyTypeForProfile, measurementProfiles, sizeChartProfile, sizeChartsFor, type BodyType, type ChartStature, type MeasurementProfile, type SizeChart } from "@tailor-frame/measurements";

/**
 * Reusable modules patterns pull in with `modules: [{ use: <id>, ... }]`:
 * every modules/<id>/v1.yaml, plus the test fixture modules.
 */
const moduleSources = import.meta.glob<string>(["../modules/*/v1.yaml", "../fixtures/modules/*/v1.yaml"], { query: "?raw", import: "default", eager: true });

function registerModules(sources: Record<string, string>): ModuleRegistry {
  const registry: ModuleRegistry = {};
  for (const [path, source] of Object.entries(sources)) {
    const parsed = parseModule(source);
    if (!parsed.module) throw new Error(`${path}:\n${parsed.diagnostics.map((item) => item.message).join("\n")}`);
    const id = parsed.module.module.id;
    if (registry[id]) throw new Error(`${path}: module ${id} is defined twice.`);
    registry[id] = parsed.module;
  }
  return registry;
}

export const modules = registerModules(moduleSources);

/** Parse a specification with the module registry, as the library and the editor both do. */
export function parsePattern(source: string) {
  return parseSpecification(source, { modules });
}

function register(source: string): RegisteredSpecification {
  const parsed = parsePattern(source);
  if (!parsed.specification) throw new Error(parsed.diagnostics.map((item) => item.message).join("\n"));
  return { source, specification: parsed.specification };
}

export const specifications = [register(pyjamaSource), register(pyjamaTrousersSource), register(tailoredShirtSource), register(tShirtSource), register(jeansSource), register(trousersSource), register(classicTrousersSource), register(swimmingTrunksSource), register(flatShirtSource), register(casualShirtSource), register(overgarmentSource), register(overallsSource), register(workJacketSource), register(jeansJacketSource), register(anorakSource), register(shirtJacketSource), register(jumperSource), register(classicShirtSource), register(suitJacketSource), register(easyFitSuitJacketSource), register(waistcoatSource), register(casualJacketSource), register(easyFitCasualJacketSource), register(overgarmentBlockSource), register(easyFitOvergarmentSource), register(circularCapeSource), register(regularCapeSource), register(carriageCapeSource), register(supremeTrousersSource), register(vestSource), register(sackCoatSource), register(overcoatSource), register(frockCoatSource), register(ridingBreechesSource), register(militaryCoatSource), register(ridingCoatSource), register(slipOnSource), register(womensSkirtSource), register(womensSwimsuitSource), register(womensBodyshaperSource), register(womensLeggingsSource), register(womensTrousersSource), register(womensJeansSource), register(womensBodiceSource), register(childFlatBodySource), register(childFlatOvergarmentSource), register(babyFlatBodySource), register(babyFlatOvergarmentSource), register(babySleepsuitSource), register(babyTwoPieceTrousersSource), register(babyOnePieceTrousersSource), register(womensTshirtSource), register(womensJacketSource), register(womensCoatSource), register(womensShirtSource), register(womensDressSource), register(rectangleSource), register(rectangleWithFinishSource)];

export function defaultsFor(specification: PatternSpecification, profileId: BodyProfileId = defaultProfileFor(specification)): PatternProject {
  return {
    projectVersion: 1,
    name: specification.pattern.name,
    madeFor: measurementProfileLabel(profileId),
    specification: { id: specification.pattern.id, version: specification.pattern.version },
    bodyMeasurements: defaultBodyMeasurements(),
    measurements: measurementsForProfile(specification, profileId),
    options: optionValues(specification.options),
  };
}

function defaultBodyMeasurements(): BodyMeasurements {
  return {
    height: 1750,
    headHeight: 230,
    headWidth: 155,
    headDepth: 195,
  };
}
